import { useState } from 'react';
import { Button, GetProp, Image, Input, InputNumber, Upload, UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { onUpdateImageSetting, onUpdateSetting } from 'modules/settings/services/settings';

// components
import TextareaSettingsComponent from '../textareaSetting/textareaSetting';

// models
import { Setting } from 'modules/settings/models/settings';

//styles
import styles from './style.module.scss';
import { PlusOutlined, RedoOutlined } from '@ant-design/icons';
import pdf from 'assets/images/pdf.png';

interface InputProps {
	setting: Setting;
	languages: { code: string; name: string }[];
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

/*
 * handledValue
 * 1- integer 						->	only integer value without float number
 * 2- number  						->	can be float number
 * 3- custom_platform_commission 	->	float number with 3 float number and maximum with 100
 * 4- images						-> 	upload image
 * 5- file							->  upload pdf file only
 */

function SettingsCategoryComponent({ setting, languages }: InputProps) {
	const { t } = useTranslation();

	const [editMode, setEditMode] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [value, setValue] = useState(setting.value);
	const [imageUrl, setImageUrl] = useState<string | null>(null); // indicate if user select an image or not and have image link

	const toggleEditMode = () => {
		setImageUrl(null);
		setEditMode(!editMode);
	};

	const handleUpdate = async (file?: any) => {
		try {
			setIsUpdating(true);

			if (setting.type == 'image' || setting.type == 'file') {
				const updateImagePayload = new FormData();
				updateImagePayload.append('_method', 'put');
				updateImagePayload.append('value', file || value);
				const response = await onUpdateImageSetting(setting.id, updateImagePayload);
				setting.value = response.data.data.value;
				setImageUrl(null);
			} else {
				await onUpdateSetting(setting.id, { value });
				setting.value = value;
			}

			setIsUpdating(false);
			toggleEditMode();
			NotificationService.success('Success', 'Settings updated successfully');
		} catch (error) {
			setIsUpdating(false);
			NotificationService.error('Ops!', 'An error occurred when updating');
		}
	};

	const beforeImageUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';

		if (!isJpgOrPng) {
			NotificationService.error('Only JPG/PNG format are acceptable');
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const beforePdfUpload = (file: FileType) => {
		if (file.type !== 'application/pdf') {
			NotificationService.error('Only PDF format are acceptable');
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		setValue(info.file);
		handleUpdate(info.file);
	};

	return (
		<div className={`sectionContainer ${styles.item}`}>
			<div className={styles.item_header}>
				<div>
					<h6>{setting.label}</h6>
				</div>

				<div>
					{editMode ? (
						<div>
							<Button color="danger" variant="link" onClick={toggleEditMode}>
								{t('COMMON.CANCEL')}
							</Button>
							{setting.type !== 'file' && setting.type !== 'image' && (
								<Button type="primary" loading={isUpdating} onClick={handleUpdate}>
									{t('COMMON.UPDATE')}
								</Button>
							)}
						</div>
					) : (
						setting.editable && (
							<Button type="primary" onClick={toggleEditMode}>
								{t('COMMON.EDIT')}
							</Button>
						)
					)}
				</div>
			</div>

			<div className={styles.item_body}>
				{/* for integer value */}
				{setting.type == 'integer' ? (
					editMode ? (
						<InputNumber
							defaultValue={setting.value}
							size="large"
							min={0}
							placeholder="Enter Value"
							style={{ width: '100%', maxWidth: 400 }}
							onChange={(value) => setValue(value)}
						/>
					) : (
						<div style={{ minHeight: 40 }}>
							<p>Value:</p>
							<p>{setting.value}</p>
						</div>
					)
				) : // for number value
				setting.type == 'number' ? (
					editMode ? (
						<InputNumber
							size="large"
							defaultValue={setting.value}
							min={0}
							step={0.01}
							placeholder="Enter Value"
							style={{ width: '100%', maxWidth: 400 }}
							formatter={(value) => value?.toString().replace(/(\.\d{2})\d*/, '$1')}
							parser={(value) => parseFloat(value as string).toFixed(2)}
							onChange={(value) => setValue(value)}
						/>
					) : (
						<div style={{ minHeight: 40 }}>
							<p>Value:</p>
							<p>{setting.value}</p>
						</div>
					)
				) : // for custom_platform_commission value
				setting.type == 'custom_platform_commission' ? (
					editMode ? (
						<InputNumber
							size="large"
							defaultValue={setting.value}
							min={0}
							max={99.999}
							step={0.001}
							placeholder="Enter Value"
							style={{ width: '100%', maxWidth: 400 }}
							formatter={(value) => value?.toString().replace(/(\.\d{3})\d*/, '$1')}
							parser={(value) => parseFloat(value as string).toFixed(3)}
							onChange={(value) => setValue(value)}
						/>
					) : (
						<div style={{ minHeight: 40 }}>
							<p>Value:</p>
							<p>{setting.value}</p>
						</div>
					)
				) : // for image value
				setting.type == 'image' ? (
					editMode ? (
						<Upload
							className={`${styles.squareInput} squareInput`}
							listType="picture-card"
							showUploadList={false}
							beforeUpload={beforeImageUpload}
							onChange={handleChange}>
							<button
								className={`${imageUrl ? styles.hasData : ''} ${styles.squareInput_button}`}
								style={{
									backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
								}}
								type="button">
								<div>
									{imageUrl ? <RedoOutlined /> : <PlusOutlined />}
									<span> {imageUrl ? 'Change' : 'Upload'}</span>
								</div>
							</button>
						</Upload>
					) : (
						<div style={{ width: 200, marginTop: 20 }}>
							<Image style={{ width: 200, border: '1px solid #eee', borderRadius: 5 }} src={setting.value} />
						</div>
					)
				) : // for file value
				setting.type == 'file' ? (
					editMode ? (
						<Upload
							className={`${styles.squareInput} squareInput`}
							listType="picture-card"
							showUploadList={false}
							beforeUpload={beforePdfUpload}
							onChange={handleChange}>
							<div className={`${styles.squareInput_button}`} style={{ background: pdf }}>
								{setting.value ? (
									<div className="d-flex flex-column justify-content-between h-100 p-2">
										<div className="p-0 m-0">
											<img src={pdf} style={{ width: 100 }} />
											<p className="mb-0 ">{setting.value.split('/')[setting.value.split('/').length - 1]}</p>
										</div>
										<Button type="primary">{t('COMMON.CHANGE')}</Button>
									</div>
								) : (
									<div>
										<PlusOutlined />
										<span> {t('COMMON.UPLOAD')}</span>
									</div>
								)}
							</div>
						</Upload>
					) : (
						<a href={setting.value} target="_blank">
							<Button type="primary" style={{ padding: '5px 10px' }}>
								{t('COMMON.DOWNLOAD')}
							</Button>
						</a>
					)
				) : // for text value
				setting.type == 'text' ? (
					editMode ? (
						setting.translatable ? (
							languages.map((lang, i) => <Input key={i} size="large" placeholder={`Enter ${lang} value`} style={{ maxWidth: 400 }} />)
						) : (
							<Input size="large" placeholder="Value" style={{ maxWidth: 400 }} />
						)
					) : setting.translatable ? (
						languages.map((lang, i) => (
							<div key={i} style={{ minHeight: 40 }}>
								<p>{lang.code.toUpperCase()}</p>
								<p>{setting.value[lang.code]}</p>
							</div>
						))
					) : (
						<div style={{ minHeight: 40 }}>
							<p>Value:</p>
							<p>{setting.value}</p>
						</div>
					)
				) : // for textarea value
				setting.type == 'textarea' ? (
					<TextareaSettingsComponent
						setting={setting}
						languages={languages}
						edit={editMode}
						onChange={(data) => {
							setValue(data);
						}}
					/>
				) : (
					<></>
				)}
			</div>
		</div>
	);
}

export default SettingsCategoryComponent;
